import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import type { UpsellBannerPropsInterface } from './types/upsell-banner.types';
import { UpsellBannerAction } from './upsell-banner-action';
import { UpsellBannerBody } from './upsell-banner-body';
import { UpsellBannerCloseButton } from './upsell-banner-close-button';
import { UpsellBannerContent } from './upsell-banner-content';
import { UpsellBannerHeader } from './upsell-banner-header';
import { UpsellBannerMedia } from './upsell-banner-media';
import { UpsellBannerRoot } from './upsell-banner-root';

/**
 * @related ['MediaObject']
 * @since 7.29.0
 */
export const UpsellBanner = forwardRef<HTMLDivElement, UpsellBannerPropsInterface>(
  (props: UpsellBannerPropsInterface, forwardedRef) => {
    const { asChild = false, body, children, action, closeButton, header, media, ...rest } = props;

    const content = useDecoratedSlotContent({
      children,
      asChild,
      alternativeContent: body,
      template: (childContent) => (
        <Fragment>
          <UpsellBanner.Media>{media}</UpsellBanner.Media>
          <UpsellBanner.Content>
            <UpsellBanner.Header>{header}</UpsellBanner.Header>
            <UpsellBanner.Body>{childContent}</UpsellBanner.Body>
          </UpsellBanner.Content>
          <UpsellBanner.Action>{action}</UpsellBanner.Action>
          <UpsellBanner.CloseButton>{closeButton}</UpsellBanner.CloseButton>
        </Fragment>
      ),
      dependencies: [body, action, closeButton, header, media],
    });

    return (
      <UpsellBanner.Root ref={forwardedRef} asChild={asChild} {...rest}>
        {content}
      </UpsellBanner.Root>
    );
  },
) as ForwardRefExoticComponent<UpsellBannerPropsInterface> & {
  Root: typeof UpsellBannerRoot;
  Media: typeof UpsellBannerMedia;
  Content: typeof UpsellBannerContent;
  Header: typeof UpsellBannerHeader;
  Body: typeof UpsellBannerBody;
  Action: typeof UpsellBannerAction;
  CloseButton: typeof UpsellBannerCloseButton;
};

UpsellBanner.displayName = 'UpsellBanner';

UpsellBanner.Root = UpsellBannerRoot;
UpsellBanner.Media = UpsellBannerMedia;
UpsellBanner.Content = UpsellBannerContent;
UpsellBanner.Header = UpsellBannerHeader;
UpsellBanner.Body = UpsellBannerBody;
UpsellBanner.Action = UpsellBannerAction;
UpsellBanner.CloseButton = UpsellBannerCloseButton;
